/** The validator's bounds (scripts/mrcs/combat-rules.js: MAX_LEVELS, MAX_LEVEL_FROM, MAX_LEVEL_TARGET, …). */
export const LEVEL_LIMITS = { count: 12, from: 1000, target: 20, initiative: 100, label: 24, condition: 40 };
const KEY_RE = /^[a-z][a-z0-9_]{0,62}$/;
const isObj = (v) => typeof v === 'object' && v !== null && !Array.isArray(v);
const isInt = (v, min, max) => typeof v === 'number' && Number.isInteger(v) && v >= min && v <= max;
const isText = (v, max) => typeof v === 'string' && v.trim().length > 0 && v.length <= max;
/** A resource's levels as the data writes them: each well formed, in rising order of `from`, each key once; else null. */
export function parseLevels(v) {
    if (!Array.isArray(v) || v.length < 1 || v.length > LEVEL_LIMITS.count)
        return null;
    const out = [];
    let last = 0;
    for (const l of v) {
        if (!isObj(l) || !Object.keys(l).every((k) => ['from', 'key', 'label', 'target', 'initiative', 'condition'].includes(k)))
            return null;
        if (!isInt(l.from, 1, LEVEL_LIMITS.from) || l.from <= last || typeof l.key !== 'string' || !KEY_RE.test(l.key) || !isText(l.label, LEVEL_LIMITS.label))
            return null;
        if (l.target !== undefined && !isInt(l.target, -LEVEL_LIMITS.target, LEVEL_LIMITS.target))
            return null;
        if (l.initiative !== undefined && !isInt(l.initiative, -LEVEL_LIMITS.initiative, LEVEL_LIMITS.initiative))
            return null;
        if (l.condition !== undefined && !isText(l.condition, LEVEL_LIMITS.condition))
            return null;
        last = l.from;
        out.push({
            from: l.from, key: l.key, label: l.label.trim(),
            ...(l.target !== undefined && l.target !== 0 ? { target: l.target } : {}),
            ...(l.initiative !== undefined && l.initiative !== 0 ? { initiative: l.initiative } : {}),
            ...(typeof l.condition === 'string' ? { condition: l.condition.trim() } : {}),
        });
    }
    return new Set(out.map((l) => l.key)).size === out.length ? out : null;
}
export const copyLevels = (ls) => ls.map((l) => ({ ...l }));
export const NO_WOUNDS = { reached: [], target: 0, initiative: 0, conditions: [], words: [], full: [] };
/** A token's wounds by the system's resources and the numbers it carries. Only a resource with a maximum has any. */
export function woundsOf(rules, resources) {
    if (!resources || !rules.some((r) => r.levels?.length))
        return NO_WOUNDS;
    const reached = [];
    const full = [];
    const words = [];
    const conditions = [];
    let target = 0;
    let initiative = 0;
    for (const r of rules) {
        const have = resources[r.key];
        if (!r.levels?.length || !have || have.max === undefined)
            continue;
        const filled = have.max - have.current;
        const at = [...r.levels].reverse().find((l) => filled >= l.from);
        if (r.end && have.current <= 0)
            full.push(r.label);
        if (!at)
            continue;
        reached.push({ resource: r.key, resourceLabel: r.label, key: at.key, label: at.label, target: at.target ?? 0, initiative: at.initiative ?? 0, condition: at.condition ?? null });
        target += at.target ?? 0;
        initiative += at.initiative ?? 0;
        const word = at.condition ?? (r.health || (at.target === undefined && at.initiative === undefined) ? at.label : `${at.label} ${r.label}`);
        if (at.condition && !conditions.includes(at.condition))
            conditions.push(at.condition);
        if (!words.includes(word))
            words.push(word);
    }
    return { reached, target, initiative, conditions, words, full };
}
/** What the DM is told of a full last resource (§5.4): more damage kills. */
export const DEAD_PAST = (label) => `${label} full: dead past this`;
//# sourceMappingURL=wounds.js.map